import { z } from '@/utils/zod'

import { Tax, TaxSchema } from './Tax'

//
// TODO : Can be split up further to make it easier for customers but ensure to map them into account types
//
export const AccountGroupSchema = z.enum([
    // Anlagevermögen
    'capitalAsset',
    // Umlaufvermögen
    'liquidAsset',
    // Bank + Kassenkonto (Umlaufvermögen)
    'cash',
    // Eigenkapital
    'equityCapital',
    // Fremdkapital
    'debtCapital',
    // Einnahmen
    'revenue',
    // Ausgaben
    'expense',
    // Vortrags- und statistisches Konto mit neutralem Saldo
    'static',
    // Vortrags- und statistisches Konto mit Soll-Saldo
    'staticDebit',
    // Vortrags- und statistisches Konto mit Haben-Saldo
    'staticCredit',
    // Debitor / Kunde
    'debtor',
    // Kreditor / Lieferant
    'creditor',
])

export type AccountGroup = z.infer<typeof AccountGroupSchema>

//
// asset     = Aktivkonto    | Active account   => Debit (+) | Credit (-) => Opening in Debit  => Balance in Credit
// liability = Passivkonto   | Passive account  => Debit (-) | Credit (+) => Opening in Credit => Balance in Debit
// revenue   = Ertragskonto  | Revenue account  => Debit (-) | Credit (+) => No opening        => Balance in Debit
// expense   = Aufwandskonto | Expense account  => Debit (+) | Credit (-) => No opening        => Balance in Credit
// debtor    = Debitor | Debtor                 => Debit (+) | Credit (-) => Opening in Debit  => Balance in Credit
// creditor  = Kreditor | Creditor              => Debit (-) | Credit (+) => Opening in Credit => Balance in Debit
// misc      = Sammelkonto | Collecting account => Debit (/) | Credit (/) => No opening        => Balance neutral
//
export const AccountTypeSchema = z.enum(['asset', 'liability', 'revenue', 'expense', 'debtor', 'creditor', 'misc'])

export type AccountType = z.infer<typeof AccountTypeSchema>

//
// permanent = Bestandskonto
// temporary = Ertragskonto
// person = Personenkonto (Debitoren + Kreditoren)
// misc = Anderes Konto
//
export const AccountClassSchema = z.enum(['permanent', 'temporary', 'person', 'misc'])

export type AccountClass = z.infer<typeof AccountClassSchema>

export type AccountDefinition = {
    type: AccountType
    clazz: AccountClass
}

export const accountGroups: Record<AccountGroup, AccountDefinition> = {
    capitalAsset: {
        type: 'asset',
        clazz: 'permanent',
    },
    liquidAsset: {
        type: 'asset',
        clazz: 'permanent',
    },
    cash: {
        type: 'asset',
        clazz: 'permanent',
    },
    equityCapital: {
        type: 'liability',
        clazz: 'permanent',
    },
    debtCapital: {
        type: 'liability',
        clazz: 'permanent',
    },
    revenue: {
        type: 'revenue',
        clazz: 'temporary',
    },
    expense: {
        type: 'expense',
        clazz: 'temporary',
    },
    static: {
        type: 'misc',
        clazz: 'misc',
    },
    staticDebit: {
        type: 'asset',
        clazz: 'misc',
    },
    staticCredit: {
        type: 'liability',
        clazz: 'misc',
    },
    debtor: {
        type: 'debtor',
        clazz: 'person',
    },
    creditor: {
        type: 'creditor',
        clazz: 'person',
    },
}

export const accountGroupDefaultTaxType: Record<AccountGroup, Tax['type'] | null> = {
    //
    // "both" is intentionally represented as null here, meaning no default account tax is allowed.
    // Allowing both causes downstream frontend ambiguity because account tax type is used to
    // filter whether an account is selectable in several booking flows.
    //
    capitalAsset: null,
    liquidAsset: null,
    cash: null,
    equityCapital: null,
    debtCapital: null,
    revenue: 'vat',
    expense: 'pre',
    static: null,
    staticDebit: null,
    staticCredit: null,
    debtor: 'vat',
    creditor: 'pre',
}

export const AccountSchema = z.object({
    number: z.number().int().min(1).max(999999999),
    label: z.string().min(1).max(200),
    // if set is taxable account
    taxCode: TaxSchema.shape.code.nullable(),
    // if set is a tax automatic account in datev
    datevAutomatic: z.boolean(),
    // if false may not be booked to
    bookable: z.boolean(),
    // standard accounts from the base accounting plan will be true, manually added ones will be false
    builtIn: z.boolean(),
})

export type Account = z.infer<typeof AccountSchema>

export const AccountRangeSchema = z.object({
    from: AccountSchema.shape.number,
    to: AccountSchema.shape.number,
    group: AccountGroupSchema,
})

export type AccountRange = z.infer<typeof AccountRangeSchema>

export const AccountCategorySchema = z.object({
    from: AccountSchema.shape.number,
    to: AccountSchema.shape.number,
    label: z.string().min(1).max(280),
    func: z.enum(['forward']).nullable(),
})

export type AccountCategory = z.infer<typeof AccountCategorySchema>

export type AccountInfo = {
    clazz: AccountClass
    group: AccountGroup
    type: AccountType
}

export type AccountWithInfo = Account & {
    info: AccountInfo
}

export type AccountWithInfoAndCategory = AccountWithInfo & {
    category: AccountCategory
}

export const AccountFilterSchema = z.object({
    groups: z.uniqueArray(AccountGroupSchema).optional(),
    types: z.uniqueArray(AccountTypeSchema).optional(),
    classes: z.uniqueArray(AccountClassSchema).optional(),
    accounts: z
        .array(z.union([z.array(AccountSchema.shape.number).min(2).max(2), AccountSchema.shape.number]))
        .optional(),
    accountName: z.string().optional(),
})

export type AccountFilter = z.infer<typeof AccountFilterSchema>
